import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

/** GET /api/authz/me: what the signed-in user holds, answered by the access engine (sign-in only, never refused). */
export type MyAccess = {
  is_super: boolean;
  roles: { key: string; name: string; via: string }[];
  groups: { id: string; title: string; source: string }[];
  /** Every permission key the user is allowed. */
  permissions: string[];
};

export function useMyAccess(userId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['authz', 'me', userId],
    queryFn: () => api.get<MyAccess>('/authz/me'),
    enabled: enabled && !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

export type AuthzGroup = { id: string; title: string; source: string; path: string };

export function useAuthzGroups(enabled = true) {
  return useQuery({
    queryKey: ['authz', 'groups'],
    queryFn: () => api.get<AuthzGroup[]>('/authz/groups'),
    enabled,
    staleTime: 60 * 1000,
  });
}

type PermissionModule = { module: string; permissions: { key: string; label: string }[] };

/** Every declared permission, flattened from the per-module catalog, for key pickers. */
export function useAuthzPermissions(enabled = true) {
  return useQuery({
    queryKey: ['authz', 'permissions'],
    queryFn: () => api.get<PermissionModule[]>('/authz/permissions'),
    select: (modules) => modules.flatMap((m) => m.permissions),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}
